import type {
  DocumentClass,
  ImportBatchRowView,
  ImportBatchStatus,
  ImportRowStatus,
  ImportTargetEntityType,
  TargetKind,
} from '@ana/contracts';
import { brl, centsToInput, dateBr, monthBr, parseMoneyInput, parseQtyInput, qty } from '../../lib/format.ts';

/*
 * What the import screens need to know about each target and canonical field. The server decides everything
 * (permissions, validation, totals); these tables only choose what to offer and how to label it. The permissions
 * mirror the manual entry of the same data (docs/data/IMPORT_ARCHITECTURE.md §3.3; targets/<alvo>.ts).
 */

export interface TargetMeta {
  label: string;
  description: string;
  /** Permission to send, review and confirm (same as the manual entry). */
  write: string;
  /** Permissions that let the user see the batches of this target. */
  read: readonly string[];
  /** Committed rows become drafts or values that only count after the workflow (shown before confirming). */
  draft: boolean;
  /** The statement needs an approved profile: it says whose statement the file is (bank, acquirer, operator). */
  needsProfile: boolean;
  /** Confirmed batch can be reverted (§3.4 Estorno). */
  revertible: boolean;
  /** Fields the target cannot do without (the server checks them again on every row). */
  required: readonly string[];
}

export const TARGET_ORDER: readonly TargetKind[] = [
  'MOVEMENT_LINE',
  'MOVEMENT_DAY',
  'STATEMENT_LINE',
  'DEPOSIT',
  'EXPENSE',
  'BUDGET_LINE',
];

export const TARGET_META: Record<TargetKind, TargetMeta> = {
  MOVEMENT_LINE: {
    label: 'Movimento diário (itens)',
    description:
      'Quantidade e valores de cada item do formulário diário. O valor importado substitui o do rascunho; nunca soma.',
    write: 'movement.edit',
    read: ['movement.read'],
    draft: true,
    needsProfile: false,
    revertible: false,
    required: ['operationalDate', 'lineItemCode'],
  },
  MOVEMENT_DAY: {
    label: 'Movimento diário (caixa apurado)',
    description: 'Caixa apurado e observações do dia, gravados no rascunho do movimento.',
    write: 'movement.edit',
    read: ['movement.read'],
    draft: true,
    needsProfile: false,
    revertible: false,
    required: ['operationalDate'],
  },
  STATEMENT_LINE: {
    label: 'Extrato bancário ou de operadora',
    description:
      'Lançamentos do extrato para a conciliação. Exige um perfil de extrato aprovado, que diz de qual banco ou operadora é o arquivo.',
    write: 'import.manage',
    read: ['finance.read'],
    draft: false,
    needsProfile: true,
    revertible: true,
    required: ['txnDate', 'amountCents'],
  },
  DEPOSIT: {
    label: 'Depósitos e borderôs',
    description: 'Depósitos com os dias de movimento que cada um cobre, ou marcados como sem vínculo.',
    write: 'deposit.manage',
    read: ['finance.read'],
    draft: false,
    needsProfile: false,
    revertible: true,
    required: ['depositDate', 'kind', 'amountCents'],
  },
  EXPENSE: {
    label: 'Despesas',
    description: 'Despesas por competência e conta gerencial, que entram na DRE gerencial.',
    write: 'expense.manage',
    read: ['finance.read'],
    draft: false,
    needsProfile: false,
    revertible: true,
    required: ['competenceDate', 'dreAccountCode', 'description', 'amountCents'],
  },
  BUDGET_LINE: {
    label: 'Orçamento mensal',
    description: 'Metas de receita bruta, de acessos e por conta gerencial, mês a mês.',
    write: 'rules.manage',
    read: ['report.read', 'rules.manage'],
    draft: false,
    needsProfile: false,
    revertible: false,
    required: ['month', 'lineKind'],
  },
};

/** Every permission that opens the import screens (menu entry and routes). */
export const IMPORT_PERMISSIONS: readonly string[] = [
  ...new Set(TARGET_ORDER.flatMap((t) => [TARGET_META[t].write, ...TARGET_META[t].read])),
];

export const writableTargets = (can: (p: string) => boolean): TargetKind[] =>
  TARGET_ORDER.filter((t) => can(TARGET_META[t].write));

// ---- canonical fields ----

export type FieldKind = 'date' | 'month' | 'cents' | 'int' | 'text' | 'code' | 'enum' | 'boolean' | 'covers';

/** Same types as the server's normalisation (apps/api/src/modules/imports/pipeline/normalize.ts FIELD_TYPES). */
export const FIELD_KIND: Record<TargetKind, Record<string, FieldKind>> = {
  STATEMENT_LINE: {
    txnDate: 'date',
    amountCents: 'cents',
    description: 'text',
    documentRef: 'text',
    externalId: 'text',
    quantity: 'int',
  },
  MOVEMENT_LINE: {
    operationalDate: 'date',
    lineItemCode: 'code',
    quantity: 'int',
    amountDueCents: 'cents',
    amountReceivedCents: 'cents',
    documentRef: 'text',
    note: 'text',
  },
  MOVEMENT_DAY: { operationalDate: 'date', cashCountedCents: 'cents', notes: 'text' },
  DEPOSIT: {
    depositDate: 'date',
    kind: 'enum',
    amountCents: 'cents',
    bankAccountCode: 'text',
    slipNumber: 'text',
    slipItemCount: 'int',
    notes: 'text',
    covers: 'covers',
    unlinked: 'boolean',
  },
  EXPENSE: {
    competenceDate: 'date',
    dreAccountCode: 'code',
    description: 'text',
    amountCents: 'cents',
    documentRef: 'text',
  },
  BUDGET_LINE: {
    month: 'month',
    lineKind: 'enum',
    dreAccountCode: 'code',
    amountCents: 'cents',
    quantity: 'int',
  },
};

export const fieldsOf = (target: TargetKind): string[] => Object.keys(FIELD_KIND[target]);

export const FIELD_LABEL: Record<string, string> = {
  txnDate: 'Data',
  operationalDate: 'Data operacional',
  depositDate: 'Data do depósito',
  competenceDate: 'Competência',
  month: 'Mês',
  amountCents: 'Valor',
  amountDueCents: 'Valor devido',
  amountReceivedCents: 'Valor recebido',
  cashCountedCents: 'Caixa apurado',
  description: 'Descrição',
  documentRef: 'Documento',
  externalId: 'Identificador',
  quantity: 'Quantidade',
  lineItemCode: 'Item',
  note: 'Observação',
  notes: 'Observações',
  kind: 'Tipo',
  bankAccountCode: 'Conta bancária',
  slipNumber: 'Borderô',
  slipItemCount: 'Itens do borderô',
  covers: 'Dias cobertos',
  unlinked: 'Sem vínculo',
  dreAccountCode: 'Conta gerencial',
  lineKind: 'Tipo de linha',
};

export const fieldLabel = (field: string): string => FIELD_LABEL[field] ?? field;

/**
 * Some of the header names the suggestion recognises for each field (synonym dictionary of the server, version
 * sinonimos-2026.10-v1 in pipeline/suggest.ts). Shown as guidance next to the mapping; the server decides.
 */
export const FIELD_SYNONYMS: Record<string, readonly string[]> = {
  txnDate: ['Data', 'Data lançamento', 'Data transação'],
  operationalDate: ['Data', 'Data operacional', 'Dia'],
  depositDate: ['Data', 'Data depósito'],
  competenceDate: ['Data', 'Competência', 'Data da despesa'],
  month: ['Mês', 'Competência', 'Período'],
  amountCents: ['Valor', 'Vlr', 'Montante', 'Total'],
  amountDueCents: ['Valor devido', 'A receber', 'Faturado'],
  amountReceivedCents: ['Valor recebido', 'Recebido', 'Arrecadado'],
  cashCountedCents: ['Caixa apurado', 'Dinheiro contado'],
  description: ['Descrição', 'Histórico', 'Memo'],
  documentRef: ['Documento', 'Doc', 'Nota fiscal'],
  externalId: ['Identificador', 'ID transação', 'NSU'],
  quantity: ['Quantidade', 'Qtd', 'Qtde'],
  lineItemCode: ['Item', 'Código item'],
  note: ['Observação', 'Obs'],
  notes: ['Observações', 'Obs'],
  kind: ['Tipo', 'Modalidade'],
  bankAccountCode: ['Conta', 'Conta bancária'],
  slipNumber: ['Borderô', 'Envelope', 'Malote'],
  slipItemCount: ['Itens do borderô', 'Qtd itens'],
  covers: ['Dias cobertos', 'Referente a'],
  unlinked: ['Sem vínculo'],
  dreAccountCode: ['Conta gerencial', 'Conta DRE', 'Plano de contas'],
  lineKind: ['Tipo de linha', 'Tipo'],
};

export const ENUM_OPTIONS: Record<string, readonly { value: string; label: string }[]> = {
  kind: [
    { value: 'DINHEIRO', label: 'Dinheiro' },
    { value: 'BORDERO', label: 'Borderô' },
    { value: 'OUTRO', label: 'Outro' },
  ],
  lineKind: [
    { value: 'GROSS_REVENUE', label: 'Receita bruta' },
    { value: 'ACCESS_QTY', label: 'Quantidade de acessos' },
    { value: 'DRE_ACCOUNT', label: 'Conta da DRE' },
  ],
};

const enumLabel = (field: string, value: string) =>
  ENUM_OPTIONS[field]?.find((o) => o.value === value)?.label ?? value;

/** Scalar as text; anything else (a list, an object) as JSON, never "[object Object]". */
const text = (v: unknown): string =>
  typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? String(v) : JSON.stringify(v);

/** A canonical value as people read it (money in R$, dates DD/MM/AAAA). Empty is a dash, never zero. */
export function displayValue(target: TargetKind, field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (FIELD_KIND[target][field]) {
    case 'cents':
      return typeof value === 'number' ? brl(value) : text(value);
    case 'int':
      return typeof value === 'number' ? qty(value) : text(value);
    case 'date':
      return typeof value === 'string' ? dateBr(value) : text(value);
    case 'month':
      return typeof value === 'string' ? monthBr(value) : text(value);
    case 'boolean':
      return value === true ? 'Sim' : 'Não';
    case 'enum':
      return enumLabel(field, text(value));
    case 'covers':
      return Array.isArray(value)
        ? value
            .map(
              (c: { operationalDate?: string; amountCents?: number }) =>
                `${dateBr(c.operationalDate ?? null)} = ${brl(c.amountCents ?? null)}`,
            )
            .join('; ')
        : text(value);
    default:
      return text(value);
  }
}

/**
 * Text typed in the correction form → canonical value for PATCH .../rows/:rowId. Money goes as centavos (the
 * server never receives a float), dates and months as ISO, quantities as integers; empty means "not informed".
 */
export function correctionValue(
  kind: FieldKind,
  text: string,
): { ok: true; value: string | number | boolean | null } | { ok: false; error: string } {
  const t = text.trim();
  switch (kind) {
    case 'cents': {
      const r = parseMoneyInput(t);
      return r.ok ? { ok: true, value: r.cents } : { ok: false, error: r.error };
    }
    case 'int': {
      const r = parseQtyInput(t);
      return r.ok ? { ok: true, value: r.value } : { ok: false, error: r.error };
    }
    case 'boolean':
      return { ok: true, value: t === 'true' };
    case 'month':
      if (t === '') return { ok: true, value: null };
      return /^\d{4}-(0[1-9]|1[0-2])$/.test(t)
        ? { ok: true, value: t }
        : { ok: false, error: 'Informe o mês no formato MM/AAAA.' };
    case 'date':
      if (t === '') return { ok: true, value: null };
      return /^\d{4}-\d{2}-\d{2}$/.test(t)
        ? { ok: true, value: t }
        : { ok: false, error: 'Informe a data no formato DD/MM/AAAA.' };
    case 'code':
      return t === '' ? { ok: true, value: null } : { ok: true, value: t.toUpperCase() };
    default:
      return { ok: true, value: t === '' ? null : t };
  }
}

/** Current canonical value of a field as text for the correction form. */
export function correctionInitial(target: TargetKind, field: string, row: ImportBatchRowView): string {
  const v = row.canonical?.[field];
  if (v === null || v === undefined) return '';
  switch (FIELD_KIND[target][field]) {
    case 'cents':
      return typeof v === 'number' ? centsToInput(v) : '';
    case 'boolean':
      return v === true ? 'true' : 'false';
    case 'covers':
      return Array.isArray(v)
        ? v
            .map(
              (c: { operationalDate?: string; amountCents?: number }) =>
                `${dateBr(c.operationalDate ?? null)}=${centsToInput(c.amountCents ?? null)}`,
            )
            .join('; ')
        : '';
    default:
      return text(v);
  }
}

// ---- statuses ----

export const BATCH_STATUS: Record<ImportBatchStatus, { label: string; tone: string }> = {
  RECEIVED: { label: 'Recebido', tone: 'info' },
  EXTRACTING: { label: 'Em leitura', tone: 'info' },
  NEEDS_MAPPING: { label: 'Aguardando mapeamento', tone: 'warn' },
  IN_REVIEW: { label: 'Em conferência', tone: 'info' },
  COMMITTED: { label: 'Confirmado', tone: 'ok' },
  DISCARDED: { label: 'Descartado', tone: 'muted' },
  FAILED: { label: 'Falha na leitura', tone: 'danger' },
  REVERTED: { label: 'Estornado', tone: 'muted' },
};

/** How the file was read, in words (summary of the batch). */
export const DOCUMENT_CLASS_LABEL: Record<DocumentClass, string> = {
  CSV: 'Texto separado por delimitador (CSV)',
  TSV: 'Texto separado por tabulação',
  XLSX: 'Planilha do Excel (.xlsx)',
  XLSM: 'Planilha do Excel com macros (.xlsm)',
  ODS: 'Planilha do LibreOffice (.ods)',
  XLS: 'Planilha antiga do Excel (.xls)',
  OFX: 'Extrato bancário OFX',
  PDF_TEXT: 'PDF com texto selecionável',
  PDF_SCAN: 'PDF digitalizado, lido por reconhecimento de texto',
  IMAGE: 'Imagem, lida por reconhecimento de texto',
  JSON: 'Arquivo JSON no formato do sistema',
  XML: 'Arquivo XML no formato do sistema',
};

/** Extensions the file picker offers; the server still decides the class by the content (detect.ts). */
export const ACCEPTED_EXTENSIONS =
  '.csv,.txt,.tsv,.xlsx,.xlsm,.xls,.ods,.ofx,.pdf,.png,.jpg,.jpeg,.json,.xml';

/**
 * OCR cells below this confidence are pointed out in the review grid. Display only: every OCR row needs a human
 * "conferida" whatever its confidence (the server refuses the commit otherwise).
 */
export const LOW_CONFIDENCE = 0.85;

/** While the worker has the file: the screen asks the server again every few seconds (§2, Entrega B). */
export const QUEUE_POLL_MS = 3000;
export const isQueued = (status: ImportBatchStatus): boolean =>
  status === 'RECEIVED' || status === 'EXTRACTING';

/**
 * Why the reading failed, by the code the batch keeps (import_batches.error_code). Unknown codes fall back to a
 * generic sentence with the code, so support can still find it.
 */
export const EXTRACTION_FAILURE: Record<string, string> = {
  PDF_ENCRYPTED:
    'O PDF está protegido por senha ou tem restrições de uso. Salve uma cópia sem proteção e envie novamente.',
  PDF_TOO_MANY_PAGES: 'O PDF passa de 30 páginas. Divida o arquivo e envie as partes.',
  PDF_PAGE_TIMEOUT:
    'Uma página do PDF demorou demais para ser lida. Divida o arquivo ou envie a planilha original.',
  PDF_PAGE_TOO_LARGE: 'Uma página do PDF é grande demais para ser lida. Digitalize em tamanho A4.',
  PDF_UNREADABLE: 'Não foi possível abrir o PDF: o arquivo está danificado ou incompleto.',
  IMAGE_UNREADABLE: 'Não foi possível abrir a imagem: o arquivo está danificado ou incompleto.',
  IMAGE_TOO_LARGE: 'A imagem passa de 40 megapixels. Reduza a resolução e envie novamente.',
  IMPORT_FORMAT: 'Não foi possível ler o arquivo: ele está danificado ou fora do formato esperado.',
  IMPORT_EMPTY: 'Nenhuma linha de dados foi encontrada no arquivo.',
  IMPORT_TOO_MANY_ROWS: 'O arquivo passa do limite de 20.000 linhas. Divida o arquivo e envie as partes.',
  IMPORT_TOO_MANY_COLUMNS: 'O arquivo passa do limite de 60 colunas: o layout não é uma tabela regular.',
  IMPORT_TOO_MANY_CELLS: 'A planilha tem células repetidas demais para ser lida. Salve só a área com dados.',
  FILE_TYPE_NOT_SUPPORTED: 'A planilha está protegida por senha. Remova a senha e envie novamente.',
  EXTRACTION_ATTEMPTS_EXHAUSTED:
    'A leitura falhou em todas as tentativas. Envie o arquivo novamente; se a falha continuar, procure o suporte.',
};

export function extractionFailure(code: string | null): string {
  return (
    (code && EXTRACTION_FAILURE[code]) ??
    `A leitura do arquivo falhou${code ? ` (código ${code})` : ''}. Envie o arquivo novamente.`
  );
}

export const ROW_STATUS: Record<ImportRowStatus, { label: string; tone: string }> = {
  PENDING: { label: 'A validar', tone: 'muted' },
  VALID: { label: 'Válida', tone: 'ok' },
  WARNING: { label: 'Com aviso', tone: 'warn' },
  ERROR: { label: 'Com erro', tone: 'danger' },
  DUPLICATE: { label: 'Duplicada', tone: 'info' },
  EXCLUDED: { label: 'Excluída', tone: 'muted' },
};

export const SUGGESTION_SOURCE: Record<string, string> = {
  PROFILE: 'perfil aprovado',
  SYNONYM: 'nome da coluna',
  MODEL: 'modelo treinado',
};

// ---- wizard ----

export const STEPS = [
  'Arquivo',
  'Tipo de dado',
  'Mapeamento',
  'Conferência',
  'Correções',
  'Duplicidades',
  'Confirmação',
  'Resultado',
] as const;

/** Steps the user may open while the batch is in review (URL ?etapa=). */
export const REVIEW_STEPS = [
  'mapeamento',
  'conferencia',
  'correcoes',
  'duplicidades',
  'confirmacao',
] as const;
export type ReviewStep = (typeof REVIEW_STEPS)[number];
const STEP_INDEX: Record<ReviewStep, number> = {
  mapeamento: 2,
  conferencia: 3,
  correcoes: 4,
  duplicidades: 5,
  confirmacao: 6,
};

/** Index in STEPS for a stored batch and the step asked for in the address. */
export function stepIndex(status: ImportBatchStatus, asked: string | null): number {
  if (status === 'NEEDS_MAPPING' || status === 'RECEIVED' || status === 'EXTRACTING') return 2;
  if (status === 'COMMITTED' || status === 'REVERTED') return 7;
  if (status === 'IN_REVIEW') {
    return asked && asked in STEP_INDEX ? STEP_INDEX[asked as ReviewStep] : 3;
  }
  return 2;
}

// ---- lineage ----

export const ENTITY_LABEL: Record<ImportTargetEntityType, string> = {
  statement_row: 'Linha de extrato',
  daily_movement: 'Movimento do dia',
  deposit: 'Depósito',
  expense: 'Despesa',
  budget_entry: 'Linha de orçamento',
};

/** Screen that shows the record a committed row produced. */
export function entityLink(row: Pick<ImportBatchRowView, 'targetEntityType' | 'canonical'>): {
  to: string;
  label: string;
} | null {
  const c = row.canonical ?? {};
  switch (row.targetEntityType) {
    case 'daily_movement': {
      const date = typeof c.operationalDate === 'string' ? c.operationalDate : null;
      return date
        ? { to: `/movimento/${date}`, label: `Abrir o movimento de ${dateBr(date)}` }
        : { to: '/movimento', label: 'Abrir o movimento diário' };
    }
    case 'statement_row':
      return { to: '/conciliacao', label: 'Abrir a conciliação' };
    case 'deposit':
      return { to: '/depositos', label: 'Abrir os depósitos' };
    case 'expense':
      return { to: '/despesas', label: 'Abrir as despesas' };
    case 'budget_entry':
      return { to: '/cadastros', label: 'Abrir o orçamento em Cadastros' };
    default:
      return null;
  }
}

/** "Linha 7 · aba Extrato" — where the row is in the original file. */
export function sourceLabel(source: ImportBatchRowView['source']): string {
  const parts = [`Linha ${source.row}`];
  if (source.sheet) parts.push(`aba ${source.sheet}`);
  if (source.page) parts.push(`página ${source.page}`);
  return parts.join(' · ');
}
